<template>
  <div class="jobs-filter-container">
    <b-row class="d-flex justify-content-between align-items-center mx-1">
      <div class="d-flex flex-row">
        <h4>
          <template v-if="loading">Loading...</template>
          <template v-else>{{ this.results.edges.length | plural('result') }}</template>
        </h4>
      </div>
      <div class="d-flex flex-row">
        <sort class="mr-4" :options="projectOrderByEnums" :value="order" @input="sortResults($event)"/>
        <toggle-filters class="py-1 px-3" v-model="showHorizontalWhere"/>
      </div>
    </b-row>

    <b-row class="mt-3" v-if="showHorizontalWhere">
      <b-col>
        <filter-block :fields="filterFields" :value="where" @input="filterResults($event)" />
      </b-col>
    </b-row>

    <loading-spinner class="my-5 text-center" v-if="loading"/>

    <template v-else-if="results && (layout === 'block' || layout === 'select-modal')">
      <template v-if="total">
        <b-row class="mt-3">
          <b-col
            class="mb-4"
            cols="12"
            v-for="entry in results.edges.filter(entry => entry && entry.id).slice((page - 1) * limit, (page - 1) * limit + limit)"
            :key="entry.id"
          >
            <project-list-item
              :project="entry"
              :where="where"
              :layout='layout'
              @selectProject="$event => { $emit('selectProject', $event) }"
            />
          </b-col>
        </b-row>
        <div v-if="!hidePagination && page && totalPages" class="filter-pagination">
          <p>Page {{ page }} of {{ totalPages }}</p>
          <b-pagination :value="page" @input="pageUpdated($event)" :per-page="limit" :total-rows="total"/>
        </div>
      </template>
      <div class="mt-3" v-else>
        <p class="text-center">No results</p>
      </div>
    </template>

    <template v-else-if="results && layout === 'page'">
      <template v-if="total">
        <b-row class="mt-3 row-cols-1 row-cols-md-2">
          <b-col
            class="mb-4"
            cols="12"
            lg="4"
            v-for="entry in results.edges.filter(entry => entry && entry.id).slice((page - 1) * limit, (page - 1) * limit + limit)"
            :key="entry.id"
          >
            <project-list-item
              class="h-100"
              :project="entry"
              :where="where"
            />
          </b-col>
        </b-row>
        <div v-if="!hidePagination && page && totalPages" class="filter-pagination">
          <p>Page {{ page }} of {{ totalPages }}</p>
          <b-pagination :value="page" @input="pageUpdated($event)" :per-page="limit" :total-rows="total"/>
        </div>
      </template>
      <div class="mt-3" v-else>
        <p class="text-center">No results</p>
      </div>
    </template>
  </div>
</template>

<script>
import LoadingSpinner from '@/components/misc/LoadingSpinner';
import ProjectListItem from '@/components/projects/ProjectListItem';
import FilterBlock from '@/components/misc/FilterBlock';
import Sort from '@/components/misc/Sort';
import ToggleFilters from '@/components/misc/ToggleFilters';
import { mapRoot } from '@/mixins/root';

export default {
  name: 'JobsFilterContainer',
  props: {
    layout: {
      type: String,
      default: () => 'block',
      validate: layout => [ 'block', 'page', 'select-modal' ].includes(layout),
    },
    loading: {
      type: Boolean,
      default: () => false,
    },
    hidePagination: {
      type: Boolean,
      default: () => false,
    },
    projects: {
      type: Array,
      default: () => ([]),
    },
    projectOrderByEnums: {
      type: Object,
      default() {
        return {
          'CREATED_DESC': 'Newest first',
          'CREATED_ASC': 'Oldest first',
        };
      }
    },
    filterFields: {
      type: Array,
      default() {
        return [
          {
            key: 'title',
            type: 'STRING',
            name: 'Title',
          },
        ];
      },
    },
  },
  components: {
    Sort,
    ToggleFilters,
    FilterBlock,
    LoadingSpinner,
    ProjectListItem,
  },
  watch: {
    projects() {
      this.results.edges = Array.isArray(this.projects) ? [ ...this.projects ] : [];
      this.sortResults(this.order);
      this.total = (this.projects ? this.projects : []).length;
      this.totalPages = Math.ceil((this.projects ? this.projects : []).length / 10);
    },
  },
  data() {
    return {
      results: {
        edges: this.projects ? this.projects : []
      },
      showHorizontalWhere: true,
      where: {
        title: '',
        skills: [],
        location: null,
      },
      order: 'CREATED_DESC',
      page: 1,
      limit: 10,
      total: (this.projects ? this.projects : []).length,
      totalPages: Math.ceil((this.projects ? this.projects : []).length / 10)
    };
  },
  computed: {
    ...mapRoot([ 'company', 'user' ]),
  },
  methods: {
    pageUpdated(page) {
      this.page = page;
      this.$scrollTo('body');
    },
    sortResults(order) {
      console.log(order);
      this.order = order;
      this.results.edges = this.results.edges.sort((a, b) => {
        if (order === 'CREATED_ASC') {
          return a.updatedAt - b.updatedAt;
        }
        if (order === 'CREATED_DESC') {
          return b.updatedAt - a.updatedAt;
        }
        return 0;
      });
    },
    filterResults(where) {
      this.where = where;
      this.results.edges = this.projects.filter(project => {
        let result = true;
        if (where.title.length > 1) {
          result = result && project.title.toLowerCase().includes(where.title.toLowerCase());
        }
        return result;
      });
      this.sortResults(this.order);
      this.total = (this.results.edges ? this.results.edges : []).length;
      this.totalPages = Math.ceil((this.results.edges ? this.results.edges : []).length / 10);
    },
  },
};
</script>

<style lang="scss">
.jobs-filter-container {

  .card.horizontal-where {
    margin-top: 10px;
    margin-bottom: 20px;
  }

  .filter-pagination {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
  }

  .vertical-where {
    .vertical-sort {
      padding: 10px 20px 0 20px;

      label {
        font-weight: normal;
      }

      .custom-radio {
        margin-right: 0;
        margin-bottom: 10px;
      }
    }

    .results-tag {
      color: rgba(49, 55, 67, 0.5);
    }
  }

}
</style>



// WEBPACK FOOTER //
// src/components/projects/FilterContainer.vue